import { useRef, useState } from 'react';
import { generateSignedUrl } from '../../api/userService';
import { X, Loader2, Paperclip } from 'lucide-react';

const SupportForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    email: '',
    issue_type: '',
    issue_category: '',
    details: '',
    attachment: null
  });

  const fileInputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const issueCategories = [
    'UX/UI',
    'Ingestion',
    'Extraction',
    'Decision Grid',
    'Decision Management',
    'Communicator',
    'Bots',
    'Other'
  ];

  const validateForm = () => {
    let newErrors = {};
    if (!formData.customer_name.trim() || formData.customer_name.length < 3) {
      newErrors.customer_name = 'Customer name must be at least 3 characters.';
    }
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email.trim() || !emailPattern.test(formData.email)) {
      newErrors.email = 'Please enter a valid email.';
    }
    if (!formData.issue_type) {
      newErrors.issue_type = 'Please select an issue type.';
    }
    if (!formData.issue_category) {
      newErrors.issue_category = 'Please select an issue category.';
    }
    if (!formData.details.trim() || formData.details.length < 10) {
      newErrors.details = 'Details must be at least 10 characters.';
    }
    if (formData.attachment) {
      const allowedFormats = ['png', 'jpg', 'jpeg', 'pdf', 'doc', 'docx'];
      const fileExtension = formData.attachment.name
        .split('.')
        .pop()
        .toLowerCase();
      if (!allowedFormats.includes(fileExtension)) {
        newErrors.attachment =
          'Invalid file type. Only images, PDFs, or Word docs are allowed.';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = e => {
    setFormData(prev => ({ ...prev, attachment: e.target.files[0] }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      setModalMessage('Submitting your request...');
      try {
        await generateSignedUrl(formData);
        setLoading(false);
        setModalMessage(
          'Your support request has been submitted successfully!'
        );
        setFormData({
          customer_name: '',
          email: '',
          issue_type: '',
          issue_category: '',
          details: '',
          attachment: null
        });
        setErrors({});
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        setLoading(false);
        setModalMessage('Submission failed. Please try again later.');
        console.error('Form submission failed', error);
      }
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/30 transition-all duration-200 ${isOpen ? 'visible opacity-100' : 'invisible opacity-0'
        }`}
      aria-modal='true'
      role='dialog'
    >
      <div className='relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-auto p-6'>
        <button
          className='absolute top-3 right-3 text-gray-400 hover:text-orange-500 transition'
          onClick={() => {
            onClose();
            setModalMessage('');
          }}
        >
          <X className='w-5 h-5' />
        </button>
        <h2 className='text-2xl font-bold text-orange-600 mb-4'>
          Support Form
        </h2>
        <form
          onSubmit={handleSubmit}
          className='flex flex-col gap-5'
          autoComplete='off'
        >
          <div>
            <label className='block font-medium mb-1 text-gray-700'>
              Customer Name
            </label>
            <input
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 ${errors.customer_name ? 'border-red-400' : 'border-gray-300'
                }`}
              name='customer_name'
              value={formData.customer_name}
              onChange={handleChange}
              placeholder='Enter your name'
            />
            {errors.customer_name && (
              <div className='text-sm text-red-500 mt-1'>
                {errors.customer_name}
              </div>
            )}
          </div>
          <div>
            <label className='block font-medium mb-1 text-gray-700'>
              Email
            </label>
            <input
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 ${errors.email ? 'border-red-400' : 'border-gray-300'
                }`}
              name='email'
              type='email'
              value={formData.email}
              onChange={handleChange}
              placeholder='Enter your email'
            />
            {errors.email && (
              <div className='text-sm text-red-500 mt-1'>{errors.email}</div>
            )}
          </div>
          <div>
            <label className='block font-medium mb-1 text-gray-700'>
              Bug or Product Enhancement
            </label>
            <select
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 ${errors.issue_type ? 'border-red-400' : 'border-gray-300'
                }`}
              name='issue_type'
              value={formData.issue_type}
              onChange={handleChange}
            >
              <option value=''>Select</option>
              <option value='Bug'>Bug</option>
              <option value='Enhancement'>Product Enhancement</option>
            </select>
            {errors.issue_type && (
              <div className='text-sm text-red-500 mt-1'>
                {errors.issue_type}
              </div>
            )}
          </div>
          <div>
            <label className='block font-medium mb-1 text-gray-700'>
              List of Possible Issues
            </label>
            <select
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 ${errors.issue_category ? 'border-red-400' : 'border-gray-300'
                }`}
              name='issue_category'
              value={formData.issue_category}
              onChange={handleChange}
            >
              <option value=''>Select</option>
              {issueCategories.map((category, idx) => (
                <option key={idx} value={category}>
                  {category}
                </option>
              ))}
            </select>
            {errors.issue_category && (
              <div className='text-sm text-red-500 mt-1'>
                {errors.issue_category}
              </div>
            )}
          </div>
          <div>
            <label className='block font-medium mb-1 text-gray-700'>
              Details
            </label>
            <textarea
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 ${errors.details ? 'border-red-400' : 'border-gray-300'
                }`}
              name='details'
              value={formData.details}
              onChange={handleChange}
              rows={3}
              placeholder='Describe your issue or enhancement request'
            />
            {errors.details && (
              <div className='text-sm text-red-500 mt-1'>{errors.details}</div>
            )}
          </div>
          <div>
            <label className='block font-medium mb-1 text-gray-700'>
              Attachment (Image, PDF, Word)
            </label>
            <div className='flex items-center gap-2'>
              <label className='flex items-center gap-2 cursor-pointer text-orange-600 hover:text-orange-800'>
                <Paperclip className='w-5 h-5' />
                <span className='underline'>Attach file</span>
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='.png,.jpg,.jpeg,.pdf,.doc,.docx'
                  onChange={handleFileChange}
                  className='hidden'
                />
              </label>
              {formData.attachment && (
                <span className='text-sm text-gray-600 truncate max-w-[120px]'>
                  {formData.attachment.name}
                </span>
              )}
            </div>
            {errors.attachment && (
              <div className='text-sm text-red-500 mt-1'>
                {errors.attachment}
              </div>
            )}
          </div>
          <button
            type='submit'
            className='w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 rounded-md transition'
            disabled={loading}
          >
            {loading ? (
              <span className='flex items-center justify-center gap-2'>
                <Loader2 className='animate-spin w-5 h-5' />
                Submitting...
              </span>
            ) : (
              'Submit'
            )}
          </button>
        </form>
        {loading ? (
          <div className='flex flex-col items-center mt-4'>
            <Loader2 className='animate-spin w-6 h-6 text-orange-600' />
            <div className='mt-2 text-gray-700'>{modalMessage}</div>
          </div>
        ) : (
          modalMessage && (
            <div
              className={`mt-4 text-center font-medium ${modalMessage.includes('failed')
                  ? 'text-red-600'
                  : 'text-green-600'
                }`}
            >
              {modalMessage}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default SupportForm;
